import { useEffect } from 'react';
import { useReactFlow } from '@xyflow/react';
import { useEditorStore } from '../state/editor-store';

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
}

export function useEditorShortcuts() {
  const { fitView, setViewport } = useReactFlow();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const state = useEditorStore.getState();
      const key = event.key.toLowerCase();
      const command = event.ctrlKey || event.metaKey;

      if (command && key === 'z') {
        event.preventDefault();
        if (event.shiftKey) state.redo();
        else state.undo();
      } else if (command && key === 'y') {
        event.preventDefault();
        state.redo();
      } else if (command && key === 'c') {
        event.preventDefault();
        state.copy();
      } else if (command && key === 'v') {
        event.preventDefault();
        state.paste();
      } else if (command && key === 'a') {
        event.preventDefault();
        state.selectAll();
      } else if (key === 'delete' || key === 'backspace') {
        event.preventDefault();
        state.deleteSelection();
      } else if (key === 'escape') {
        state.clearSelection();
      } else if (key === 'f' && !command && !event.altKey) {
        event.preventDefault();
        const graph = state.graph;
        if (graph?.nodes.length) void fitView({ duration: 220, padding: 0.18 });
        else void setViewport({ x: 0, y: 0, zoom: 1 }, { duration: 220 });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [fitView, setViewport]);
}

export function useUnsavedChangesGuard(dirty: boolean) {
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
}
